import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { login } from '../store/authSlice';
import Loading from './common/Loading';
import { Formik } from 'formik';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { IconEye, IconEyeOff } from '@tabler/icons-react';

export default function Login() {
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();
    const accessToken = useSelector(state => state.auth.accessToken);
    const loading = useSelector(state => state.auth.loading);

    const dispatch = useDispatch();

    // Navigate to admin portal if user is already logged in
    useEffect(() => {
        if (accessToken != null) {
            navigate('/');
        }
    }, [accessToken, navigate]);

    const onLogin = (values) => {
        dispatch(
            login(
                values['username'],
                values['password'],
            )
        );
    }

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-2xl font-bold text-center">Pro English</CardTitle>
                </CardHeader>
                <CardContent>
                    <Formik
                        initialValues={{ username: '', password: '', }}
                        validate={values => {
                            const errors = {};
                            if (!values.username || values.username.length === 0) {
                                errors.username = 'Required';
                            }
                            if (!values.password || values.password.length === 0) {
                                errors.password = 'Required';
                            }
                            return errors;
                        }}
                        onSubmit={(values, { setSubmitting }) => {
                            onLogin(values);
                        }}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <label htmlFor="username" className="text-sm font-medium text-foreground">
                                        Username
                                    </label>
                                    <Input
                                        id="username"
                                        name="username"
                                        type="text"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.username}
                                        className={errors.username ? "border-red-500" : ""}
                                    />
                                    {errors.username && (
                                        <p className="text-sm text-red-500">{errors.username}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="password" className="text-sm font-medium text-foreground">
                                        Password
                                    </label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            value={values.password}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={`pr-10 ${errors.password ? "border-red-500" : ""}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={togglePasswordVisibility}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                        >
                                            {showPassword ? (
                                                <IconEyeOff className="h-4 w-4" />
                                            ) : (
                                                <IconEye className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <p className="text-sm text-red-500">{errors.password}</p>
                                    )}
                                </div>

                                <div className="flex items-center justify-between pt-4">
                                    <Button type="submit" className="w-full" disabled={loading}>
                                        {loading ? "Logging in..." : "Login"}
                                    </Button>
                                </div>

                                {loading && (
                                    <div className="flex justify-center mt-4">
                                        <Loading />
                                    </div>
                                )}
                            </form>
                        )}
                    </Formik>
                </CardContent>
            </Card>
        </div>
    );
}